import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import FormButton from 'components/form-components/FormButton';
import TableComponent from 'components/table-component-new/Table';
import { useTranslation } from 'react-i18next';
import { submissionRecipientHeaderList } from '../utils';
import AddRecipientsModal from './AddRecipientsModal';
import toast from 'react-hot-toast';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import { CallAPI } from 'actions/General';

const SendWebPageModal = ({ handleClose, webPagePayloadDetails, submissionId }) => {
  const { t } = useTranslation();
  const [recipientList, setRecipientList] = useState([]);
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTemplate = async () => {
    let result = await CallAPI('GET_ALL_WEB_PAGE_CUSTOM_TEMPLATES');
    if (result.status) {
      setTemplateOptions([
        { value: null, displayValue: t('text_default') },
        ...result.data.map((t) => ({ value: t._id, displayValue: t.title })),
      ]);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, []);

  const sendPDFCopyToReceivers = async (receivers) => {
    const result = await CallAPI('GET_PREVIEW_ID', {
      previewOf: 'submission',
      formId: webPagePayloadDetails.formId,
      submissionId: submissionId,
    });

    if (result.status) {
      const payload = {
        formId: webPagePayloadDetails.formId,
        previewId: result.data._id,
        submissionId: submissionId,
        responseEnabled: webPagePayloadDetails.webPageResponseAllowed,
        ...receivers,
        messageDetail: {
          formTitle: webPagePayloadDetails.formTitle,
          referenceNumber: webPagePayloadDetails.referenceNumber,
          submissionDate: webPagePayloadDetails.submissionDate,
          userName: webPagePayloadDetails.userName,
        },
      };

      await CallAPI('SEND_WEB_PAGE', payload, null, setLoading);
    }
    handleClose();
  };

  const onSend = async (receivers) => {
    toast.promise(
      sendPDFCopyToReceivers(receivers),
      {
        loading: 'Sending PDF Copy...',
        success: <span className='theme-text-white-1'>PDF Copy sent successfully.</span>,
        error: <span className='theme-text-white-1'>Something went wrong.</span>,
      },
      { style: { borderRadius: '8px', background: '#000', color: '#fff' } },
    );
  };

  const handleSubmit = async () => {
    const recipients = [];

    for (let i = 0; i < recipientList.length; i++) {
      const item = recipientList[i];

      if (!item.channels?.length) {
        toast.error(`Found row ${i + 1} with no channel`);
        return;
      }

      const { email, mobileNumber, countryCode, channels, template } = item;

      const formattedNumber =
        mobileNumber && countryCode
          ? `${countryCode.replace('+', '')}${mobileNumber}`.replace(/\s+/g, '')
          : null;

      channels.forEach((channel) => {
        if (channel === 'email' && email) {
          recipients.push({
            type: 'email',
            value: email,
            customTemplateId: template || null,
          });
        }

        if ((channel === 'sms' || channel === 'whatsapp') && formattedNumber) {
          recipients.push({
            type: channel,
            value: formattedNumber,
            customTemplateId: template || null,
          });
        }
      });
    }

    onSend({ recipients });
  };

  const deleteRowCallback = (id) => {
    setRecipientList((prevList) => prevList.filter((item) => item._id !== id));
  };

  const submissionChannelCallback = useCallback((rowIndex, channels) => {
    setRecipientList((prevList) =>
      prevList.map((item) => (item.id === rowIndex ? { ...item, channels } : item)),
    );
  }, []);

  const setTemplate = useCallback((rowId, id) => {
    setRecipientList((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, template: id } : item)),
    );
  }, []);

  return (
    <>
      <AlertModal
        show={true}
        actionButtons={[]}
        noPadding
        size='xl'
        backdropClassName={'backdrop-overlap'}
      >
        <div className='w-100'>
          <div className='p-3 d-flex justify-content-between border-bottom-grey-10-h-1'>
            <span className='theme-font-inter-semi-bold theme-size-1_2 theme-text-black-1'>
              {t('text_send_pdf_copy')}
            </span>
            <img className='p-2 cursor-pointer' src={crossGreyIcon} onClick={handleClose} />
          </div>

          <div className='p-3 d-flex flex-column gap-2'>
            <div className='d-flex justify-content-between pb-2'>
              <div className='d-flex gap-3'>
                <div className='d-flex gap-2 align-items-center'>
                  <span className='width-max-content'>{t('text_ref_no')}</span>
                  <span className='width-max-content theme-background-white-3 lh-1 p-2 rounded-sm'>
                    {webPagePayloadDetails.referenceNumber}
                  </span>
                </div>
                <div className='d-flex gap-2 align-items-center'>
                  <img src={userGreyIcon} />
                  <span className='width-max-content theme-background-white-3 lh-1 p-2 rounded-sm'>
                    {webPagePayloadDetails.userName}
                  </span>
                </div>
              </div>
              <div
                className='d-flex align-items-center gap-2 cursor-pointer'
                onClick={() => setShowAddRecipientModal(true)}
              >
                <img src={plusIcon} />
                <label className='theme-text-green-1 cursor-pointer'>
                  {t('button_add_recipients')}
                </label>
              </div>
            </div>

            <TableComponent
              header={submissionRecipientHeaderList}
              data={recipientList}
              loader={false}
              isFieldSelectionEnabled={false}
              isActionsEnabled={false}
              isSelectionEnabled={false}
              headerCellBorder='vertical'
              bodyCellBorder='both'
              noWrap
              props={{
                submissionChannelCallback,
                templateDetails: { setTemplate, templateOptions },
                deleteRowCallback,
                translateValueFunc: (val) => t(val),
              }}
              noDataComponent={
                <div className='w-100 d-flex flex-column gap-2 justify-content-center align-items-center py-3'>
                  <span className='theme-text-black-1 theme-font-inter-semi-bold theme-size-1_3'>
                    {t('text_recipients_empty')}!
                  </span>
                  <span>{t('text_select_recipients_and_sending_channels')}</span>
                  <div
                    className='d-flex align-items-center gap-2 cursor-pointer'
                    onClick={() => setShowAddRecipientModal(true)}
                  >
                    <img src={plusIcon} />
                    <label className='theme-text-green-1 cursor-pointer'>
                      {t('button_add_recipients')}
                    </label>
                  </div>
                </div>
              }
            />
          </div>

          <div className='p-3 d-flex justify-content-end border-top-grey-10-h-1'>
            <div className='d-flex gap-3'>
              <FormButton text='button_close' variant='green-2' onClick={handleClose} />
              <FormLoadingButton
                text='button_send'
                variant='green-1'
                loading={loading}
                onClick={handleSubmit}
                disabled={!recipientList.length}
              />
            </div>
          </div>
        </div>
      </AlertModal>

      {showAddRecipientModal && (
        <AddRecipientsModal
          handleClose={() => setShowAddRecipientModal(false)}
          setRecipientList={setRecipientList}
          templateOptions={templateOptions}
        />
      )}
    </>
  );
};

SendWebPageModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  webPagePayloadDetails: PropTypes.object,
  submissionId: PropTypes.string.isRequired,
};

export default SendWebPageModal;
